'use client'

/*
 * BellCurveChart
 *
 * The interactive bell curve widget on the homepage.
 *
 * HOW THIS COMPONENT IS STRUCTURED:
 * - The server pre-computes the curve data and passes it in as a prop. That
 *   means the SVG bars are in the initial HTML and are visible to crawlers and
 *   to users instantly - no JavaScript required to paint them.
 * - This client component only adds the hover behaviour on top: it attaches
 *   onMouseEnter / onMouseMove / onMouseLeave handlers and renders a floating
 *   tooltip when the user hovers a bar.
 * - Crucially, the <svg> itself and all its <rect> bars come from the server-
 *   rendered markup - React takes over ("hydrates") those existing elements
 *   rather than creating them fresh. So the LCP paint happens from HTML before
 *   this JavaScript runs.
 */

import { useState } from 'react'

function getIQLabel(iq) {
  if (iq >= 130) return 'Very Superior'
  if (iq >= 120) return 'Superior'
  if (iq >= 110) return 'High Average'
  if (iq >= 90) return 'Average'
  if (iq >= 80) return 'Low Average'
  if (iq >= 70) return 'Borderline'
  return 'Extremely Low'
}

export default function BellCurveChart({ bellCurveData }) {
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

  const handleMouseMove = (e) => {
    const target = e.currentTarget
    const clientX = e.clientX
    const clientY = e.clientY

    if (!target) return

    requestAnimationFrame(() => {
      const rect = target.getBoundingClientRect()
      const x = clientX - rect.left
      const y = clientY - rect.top
      setMousePosition({ x, y })
    })
  }

  return (
    <div className="relative h-48 lg:h-64 w-full">
      <svg
        viewBox="0 0 400 200"
        className="w-full h-full"
        onMouseLeave={() => setHoveredPoint(null)}
        onMouseMove={handleMouseMove}
      >
        <defs>
          <linearGradient id="modernGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#1d4ed8" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.5" />
          </linearGradient>
        </defs>

        {bellCurveData.map((point, i) => {
          const x = (i / (bellCurveData.length - 1)) * 380 + 10
          const barHeight = point.height * 1.2
          const y = 170 - barHeight
          const barWidth = 4

          return (
            <rect
              key={i}
              x={x - barWidth / 2}
              y={y}
              width={barWidth}
              height={barHeight}
              fill="url(#modernGradient)"
              className="cursor-pointer transition-all duration-200 hover:opacity-80"
              onMouseEnter={() => setHoveredPoint(point)}
            />
          )
        })}

        {/* Key IQ markers */}
        <line x1="200" y1="20" x2="200" y2="170" stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
        <text x="200" y="15" textAnchor="middle" className="text-xs fill-gray-600 font-medium">100</text>

        <line x1="120" y1="20" x2="120" y2="170" stroke="#94a3b8" strokeWidth="1" strokeDasharray="2,2" opacity="0.3" />
        <text x="120" y="15" textAnchor="middle" className="text-xs fill-gray-500">85</text>

        <line x1="280" y1="20" x2="280" y2="170" stroke="#94a3b8" strokeWidth="1" strokeDasharray="2,2" opacity="0.3" />
        <text x="280" y="15" textAnchor="middle" className="text-xs fill-gray-500">115</text>

        {/* Axis */}
        <line x1="10" y1="175" x2="390" y2="175" stroke="#e5e7eb" strokeWidth="2" />
        <text x="10" y="190" textAnchor="start" className="text-xs fill-gray-400 font-medium">40</text>
        <text x="390" y="190" textAnchor="end" className="text-xs fill-gray-400 font-medium">160</text>
      </svg>

      {/* Mouse-following tooltip - only renders when hovering */}
      {hoveredPoint && (
        <div
          className="absolute bg-gray-900 text-white p-3 rounded-lg shadow-lg z-10 pointer-events-none transition-all duration-75"
          style={{
            left: `${Math.min(mousePosition.x + 15, 350)}px`,
            top: `${Math.max(mousePosition.y - 60, 10)}px`,
          }}
        >
          <div className="text-sm space-y-1 whitespace-nowrap">
            <div className="font-bold">IQ: {hoveredPoint.iq}</div>
            <div className="text-gray-300">{hoveredPoint.percentile}th percentile</div>
            <div className="text-xs text-gray-400">{getIQLabel(hoveredPoint.iq)}</div>
          </div>
        </div>
      )}
    </div>
  )
}